import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export interface FreeCodeRow {
  id: string;
  code: string;
  note: string | null;
  max_uses: number;
  uses_count: number;
  active: boolean;
  created_at: string;
}

async function requireAdmin(supabase: any, userId: string) {
  const { data: isAdmin } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden");
}

export const listFreeCodes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("free_review_codes")
      .select("id, code, note, max_uses, uses_count, active, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { codes: (data ?? []) as FreeCodeRow[] };
  });

function generateCode() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let suffix = "";
  for (let i = 0; i < 6; i++) {
    suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `TEYE-${suffix}`;
}

export const createFreeCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { note?: string; maxUses?: number; code?: string }) => input)
  .handler(async ({ data, context }) => {
    await requireAdmin(context.supabase, context.userId);
    const code = (data.code?.trim() || generateCode()).toUpperCase();
    const maxUses = Math.max(1, Math.min(10000, Math.floor(data.maxUses ?? 1)));
    const { error } = await context.supabase.from("free_review_codes").insert({
      code,
      note: data.note?.trim() || null,
      max_uses: maxUses,
      created_by: context.userId,
    });
    if (error) {
      if (error.code === "23505") throw new Error("That code already exists.");
      throw new Error(error.message);
    }
    return { code };
  });

export const setFreeCodeActive = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string; active: boolean }) => input)
  .handler(async ({ data, context }) => {
    await requireAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("free_review_codes")
      .update({ active: data.active })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export type RedeemResult =
  | { ok: true; code: string; free_until?: string }
  | {
      ok: false;
      error:
        | "not_signed_in"
        | "invalid_code"
        | "code_inactive"
        | "already_redeemed"
        | "code_exhausted";
    };

export const redeemFreeCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { code: string }) => input)
  .handler(async ({ data, context }) => {
    const { data: result, error } = await context.supabase.rpc(
      "redeem_free_review_code",
      { p_code: data.code },
    );
    if (error) throw new Error(error.message);
    return result as RedeemResult;
  });
